import { VARIABLE_IDS } from '@/components/admin/variables/contracts';
import { cn } from '@/lib/utils';
import type { PillPaint, VariableDescriber } from '@/lib/variable-pill';
import { PILL_INK_CLASS, PILL_SHAPE_CLASS, pillOf } from '@/lib/variable-pill';
import { experienceSegmentsOf } from '@matter_extractor/shared';

/**
 * LA pastille, dessinée une fois pour les cinq zones (refonte modulaire §8.6 ;
 * grill Variables du 2026-09-16, D5 ; maquette `variables.html`, planche 2).
 *
 * Two ways of putting it on screen, and they emit the SAME element:
 *
 *   - `buildPillElement`, imperative, for the three painted `contentEditable`
 *     surfaces (the WYSIWYG, the plain painted field, the protocol's direction):
 *     React does not own their children, so the pill is built by hand and read
 *     back by `serializePainted` or `scanEditable` through its `data-variable`;
 *   - `VariablePill`, a component, for the surfaces React does own - the
 *     read-only panel « Ce que l'interviewer recevra », the lines of the menu.
 *
 * ## The pill is an ATOM
 *
 * `contenteditable="false"` is what keeps a caret out of the name - a token
 * cut in half by a keystroke would be two halves nothing resolves - and it is
 * set as an ATTRIBUTE and not through the property: jsdom does not implement
 * the setter, so the whole design would be absent in every unit test while
 * being present in a browser. `tabindex="0"` is what D6 needs for the keyboard:
 * Tab reaches the pill and the fiche opens on its focus.
 *
 * What the pill knows is in `data-variable` - `champ:<id>`, the token without
 * its braces - and nothing else: the fiche is opened by the zone around it,
 * which reads the mark and asks its own describer. A pill carries no handler,
 * so a pill built in an effect and a pill rendered by React behave alike.
 *
 * ## A focusable atom is a caret trap, and `caretAfterPill` is the way out
 *
 * The two attributes above are each right on their own and ruinous together
 * under a mouse, which is the whole of issue #138. A `tabindex` makes the pill
 * a focusable island; inside a `contentEditable`, a `mousedown` on it therefore
 * moves `document.activeElement` ONTO the pill and drops the caret inside its
 * own non-editable label. The caret is then in a place that edits nothing:
 * « Retour arrière » has nothing to delete, the arrows do not move and the next
 * keystroke is swallowed. Only the outermost pixels of the pill escaped it,
 * because there the browser snapped the caret to the neighbouring text node -
 * which is why the defect read as « la variable ne se supprime plus » rather
 * than as a focus bug.
 *
 * The zone therefore takes the `mousedown` itself (`useVariableFiche`): a
 * `preventDefault` there refuses BOTH the focus theft and the native caret
 * placement in one gesture - they are the same default - and this function puts
 * the caret back where a click on a pill means to put it, just after the atom.
 * `tabindex` survives untouched, so D6's Tab path does too; and the `click`
 * still fires, so D6's « un clic la fixe » is the same click as before.
 */

export function pillClassName(ink: PillPaint['ink']): string {
  return cn(
    PILL_SHAPE_CLASS,
    PILL_INK_CLASS[ink],
    'cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  );
}

/** The pill as the three painted surfaces build it, by hand - see the head. */
export function buildPillElement(paint: PillPaint): HTMLSpanElement {
  const pill = document.createElement('span');
  pill.setAttribute('contenteditable', 'false');
  pill.setAttribute('tabindex', '0');
  pill.dataset.testid = VARIABLE_IDS.pill;
  pill.dataset.variable = paint.token;
  pill.dataset.ink = paint.ink;
  pill.className = pillClassName(paint.ink);
  pill.textContent = paint.label;
  return pill;
}

/**
 * Place the caret just AFTER a pill, inside the host that edits it (#138).
 *
 * `setStartAfter` names the point BESIDE the atom - the pill's parent, at the
 * index following it - which is the very point `domPointAt` computes for
 * `atom.end`, so the caret lands where the model says the token ends. It needs
 * no text node of its own on either side: a pill that opens or closes its
 * paragraph is addressed by that parent-and-offset pair like any other.
 *
 * The host is focused BEFORE the range is applied, and the order is not
 * cosmetic: focusing a `contentEditable` moves the selection to its start, so a
 * range installed first would be thrown away by the very call meant to make it
 * an editing caret.
 */
export function caretAfterPill(host: HTMLElement, pill: HTMLElement): void {
  const selection = window.getSelection();
  if (selection === null) return;
  const range = document.createRange();
  range.setStartAfter(pill);
  range.collapse(true);
  host.focus();
  selection.removeAllRanges();
  selection.addRange(range);
}

/**
 * Turn every token left in the TEXT NODES of a painted surface into a pill.
 *
 * The WYSIWYG is painted by `renderRichText`, the pure function of `shared`
 * that the reader paints with (§9.2), and that function stays untouched: it
 * escapes what it emits, so no pill can come out of it. The tokens it leaves as
 * text - `{{champ:<id>}}`, the stored spelling - are found HERE, on the DOM
 * the field owns, by walking its text nodes: `experienceSegmentsOf` runs on
 * decoded text and never on markup, so an id that happened to hold a `<`
 * cannot swallow a tag. A pill inserted inside a `<strong>` stays inside it,
 * which is « une variable dans une phrase en gras reste en gras ».
 *
 * Nothing is padded around a pill that opens or closes its block, and that was
 * measured rather than assumed (#138): Chromium places a caret beside such a
 * pill at its parent's own offset, and deletes the atom whole from there. The
 * empty text nodes an earlier reading called for would have bought nothing and
 * cost two nodes that `scanEditable` and `serializePainted` must both keep
 * ignoring.
 */
export function paintTokensInto(root: HTMLElement, describe: VariableDescriber): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const texts: Text[] = [];
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    if (node instanceof Text && node.data.includes('{{')) texts.push(node);
  }
  for (const text of texts) {
    const segments = experienceSegmentsOf(text.data);
    if (!segments.some((segment) => segment.kind === 'variable')) continue;
    const fragment = document.createDocumentFragment();
    for (const segment of segments) {
      fragment.append(
        segment.kind === 'text'
          ? document.createTextNode(segment.text)
          : buildPillElement(pillOf(describe(segment.variable))),
      );
    }
    text.replaceWith(fragment);
  }
}

export interface VariablePillProps {
  readonly paint: PillPaint;
  /**
   * Whether the pill can open its fiche - reachable at the keyboard and marked
   * for the zone's hover. False on a line of the menu, which is itself a
   * button: a focusable inside a button is markup no reader has to read.
   */
  readonly interactive?: boolean;
  readonly className?: string;
}

/** The pill as React draws it, where React owns the surface. */
export function VariablePill({ paint, interactive = true, className }: VariablePillProps) {
  return (
    <span
      {...(interactive
        ? { 'data-testid': VARIABLE_IDS.pill, 'data-variable': paint.token, tabIndex: 0 }
        : {})}
      data-ink={paint.ink}
      className={cn(pillClassName(paint.ink), className)}
    >
      {paint.label}
    </span>
  );
}
